import i18n from '@/i18n/i18n'
import FarmerService from '@/services/retailer/FarmerService.js'
import { build_query, errorMessage } from '@/utilities/helpers.js'

import { FARMER_ERROR_MESSAGES, REFERRAL_CODE_ERROR_MESSAGES } from '@/constants/errorMessages'
import { OTP_TYPE } from '@/constants/otpType'

const { t } = i18n.global

export const allowedFilters = ['fullname', 'by_company_id', 'has_active_pre_approved_limit']
export const allowedSorts = 'firstname'
export const allowedIncludes = ['companies', 'activePreApprovedLimit']

export const namespaced = true

export const state = {
  farmers: null,
  farmersWithPreApprovedLimit: null,
  farmer: {},
  uploadGdprDocumentPercentage: null,
  farmers_have_limit: null,
  validated: null,
  isLoading: false,
  meta: null,
  statistics: {}
}
export const mutations = {
  SET_FARMERS(state, farmers) {
    state.farmers = farmers.data
    state.farmersWithPreApprovedLimit = state.farmers.filter(farmer => farmer.active_pre_approved_limit !== null)
    state.meta = farmers.meta
  },
  SET_FARMER(state, farmer) {
    state.farmer = farmer
  },
  SET_FARMERS_HAVE_LIMIT(state, farmers) {
    state.farmers_have_limit = farmers
  },
  SET_VALIDATED(state, response) {
    state.validated = response
  },
  SET_STATISTICS(state, statistics) {
    state.statistics = statistics
  },
  SET_UPLOAD_GDPR_DOCUMENT_PERCENTAGE(state, uploadGdprDocumentPercentage) {
    state.uploadGdprDocumentPercentage = uploadGdprDocumentPercentage
  }
}
export const getters = {
  getFarmerById: state => id => {
    return state.farmers ? state.farmers.find(farmer => farmer.id === id) : false
  }
}

export const actions = {
  setUploadGdprDocumentPercentage({ commit }, percentage) {
    commit('SET_UPLOAD_GDPR_DOCUMENT_PERCENTAGE', percentage)
  },
  getFarmers({ commit, dispatch }, query) {
    query['has_sales'] = true
    let newQuery = build_query(query, allowedFilters, allowedIncludes, allowedSorts)
    return new Promise((resolve, reject) => {
      FarmerService.getFarmers(newQuery)
        .then(response => {
          commit('SET_FARMERS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('constants.error_messages.farmer_error_messages.farmers_error') + ' ' + errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFarmer({ commit, state, getters }, id) {
    if (id === state.farmer.id) return farmer
    let farmer = getters.getFarmerById(id)
    if (farmer) {
      commit('SET_FARMER', farmer)
      return farmer
    } else {
      return new Promise(resolve => {
        FarmerService.getFarmer(id).then(response => {
          commit('SET_FARMER', response.data)
          resolve(response.data)
        })
      })
    }
  },
  getFarmersHaveLimit({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      FarmerService.getFarmersHaveLimit()
        .then(response => {
          commit('SET_FARMERS_HAVE_LIMIT', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: FARMER_ERROR_MESSAGES.getFarmersHaveLimitError + errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  validate({ commit, rootState }, farmer) {
    return new Promise(resolve => {
      if (state.isLoading) {
        resolve(false)
        return
      }

      state.isLoading = true

      let parameters = {
        firstname: farmer.firstname,
        lastname: farmer.lastname,
        birthdate: farmer.birthdate
      }

      FarmerService.validate(rootState.application.farmer_id, parameters)
        .then(response => {
          commit('SET_VALIDATED', response.data)
          resolve(response.data)
        })
        .catch(() => {
          resolve(false)
        })
        .then(() => {
          state.isLoading = false
        })
    })
  },
  getStatistics({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      FarmerService.getStatistics(id)
        .then(response => {
          commit('SET_STATISTICS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: FARMER_ERROR_MESSAGES.getStatisticsError + errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  sendVerifyPhoneOTPCode({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      FarmerService.sendVerifyPhoneOTPCode(params.tckn, params.phone)
        .then(response => {
          if (params.resend) {
            const notification = {
              type: 'success',
              message: t('store.retailer.farmer.confirmation_code_sent')
            }
            dispatch('notification/add', notification, {
              root: true
            })
          }
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.retailer.farmer.confirmation_code_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  verifyPhoneOTPCode({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      FarmerService.verifyPhoneOTPCode(params.tckn, params.code, params.phone)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          let message
          if (params.isGuarantor === true) {
            message = t('store.retailer.guarantor.invalid_confirmation_code')
          } else {
            message = t('store.retailer.farmer.invalid_confirmation_code')
          }

          const notification = {
            type: 'danger',
            message: message + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  checkPolicyAcceptance({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      FarmerService.checkPolicyAcceptance(
        params.tckn,
        params.phone,
        params.send_otp,
        params.send_gdpr_consent_link,
        params.application_id ? params.application_id : null,
        params.sales_channel_id ? params.sales_channel_id : null,
        params.tarfin_company_type_for_ro ? params.tarfin_company_type_for_ro : null
      )
        .then(response => {
          if (params.resend) {
            const notification = {
              type: 'success',
              message: t('store.retailer.farmer.confirmation_code_sent')
            }
            dispatch('notification/add', notification, {
              root: true
            })
          }
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: params.resend
              ? t('store.retailer.farmer.confirmation_code_error') + errorMessage(err)
              : t('store.retailer.farmer.privacy_policy_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  verifyPolicyAcceptance({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      FarmerService.verifyPolicyAcceptance(params.tckn, params.code)
        .then(response => {
          if (response.data.is_verified) {
            resolve(response.data)
          } else {
            const notification = {
              type: 'danger',
              message: t(
                params.isGuarantor
                  ? 'store.retailer.guarantor.invalid_confirmation_code'
                  : 'store.retailer.farmer.invalid_confirmation_code'
              )
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(response.data)
          }
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  verifyConfirmationCodeAcceptance({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      FarmerService.verifyConfirmationCodeAcceptance(params.phone, params.otp_type, params.code, params.application_id)
        .then(response => {
          if (response.result === 'success') {
            resolve('success')
          } else {
            const notification = {
              type: 'danger',
              message: t(
                params.otp_type === OTP_TYPE.GUARANTOR_COMPLETE
                  ? 'store.retailer.guarantor.invalid_confirmation_code'
                  : 'store.retailer.farmer.invalid_confirmation_code'
              )
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(response.data)
          }
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  isGdprConsentLinkExist({ dispatch }, gdpr_consent_link_hash) {
    return new Promise((resolve, reject) => {
      FarmerService.isGdprConsentLinkExist(gdpr_consent_link_hash)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.retailer.farmer.gdpr_consent_link_invalid')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  approveGdprConsentLinkAndCreateOtp({ dispatch }, gdpr_consent_link_hash) {
    return new Promise((resolve, reject) => {
      FarmerService.approveGdprConsentLinkAndCreateOtp(gdpr_consent_link_hash)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.retailer.farmer.gdpr_consent_link_invalid')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateGdprConsentLinkToOpened({ dispatch }, gdpr_consent_link_hash) {
    return new Promise((resolve, reject) => {
      FarmerService.updateGdprConsentLinkToOpened(gdpr_consent_link_hash)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.retailer.farmer.gdpr_consent_link_invalid')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  uploadGdprDocument({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      FarmerService.uploadGdprDocument(params.tckn, params.file)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getQrCodeForGdprDocumentUpload({ dispatch }, payload) {
    return new Promise((resolve, reject) => {
      FarmerService.getQrCodeForGdprDocumentUpload(payload.tckn, payload.phone)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewGdprDocument({ commit, dispatch }, gdprConsentDocument) {
    return new Promise((resolve, reject) => {
      FarmerService.viewGdprDocument(gdprConsentDocument.id, gdprConsentDocument.contentType)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  checkGdprConsentDocument({ commit, dispatch }, tckn) {
    return new Promise((resolve, reject) => {
      FarmerService.checkGdprConsentDocument(tckn)
        .then(response => {
          if (!response || !response.document) {
            resolve(null)
          }
          resolve(response.document)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  checkGuarantorPermit({ }, params) {
    return new Promise((resolve, reject) => {
      FarmerService.checkGuarantorPermit(params.tckn, params.cash_price)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  checkReferralCode({ dispatch }, code) {
    return new Promise((resolve, reject) => {
      FarmerService.checkReferralCode(code)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: REFERRAL_CODE_ERROR_MESSAGES.checkReferralCode
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
